import React, { useCallback, useMemo, useState } from "react";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import { ActorKind, ActorType, SourcePredicate } from "@common/types/auditLog";
import { UserSchema } from "@common/schemas/user.schema";
import { KICK_SUPPORT_LABEL } from "@common/constants/actor";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerSectionData,
} from "@design-system/Picker/Picker.types";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { HStack } from "@design-system/HStack/HStack";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { HistoryFilters } from "@hooks/view/useHistoryPageFiltersCache";
import { useConnectedIntegrations } from "@components/pageHeader/filters/Tabs/ActorFilter/useConnectedIntegrations";
import type { ActivityFilterRows } from "@components/activityTimeline/filters/activityFilters.types";
import {
    FILTER_AGENT_ICONS,
    FilterAgentKind,
    getAgentFilterLabel,
    SERIES_FILTER_ICONS,
    SERIES_FILTER_LABELS,
    toSeriesFilterValue,
} from "@components/activityTimeline/activityTimelineSeriesFilter";
import { useIsActiveWorkspacePlatformEnabled } from "@hooks/useIsActiveWorkspacePlatformEnabled";
import {
    arePredicatesSubsetOf,
    getSourcePredicatesForActor,
    mergePredicates,
} from "../utils/actorPredicates";
import { CLEARED_ACTOR_FILTERS } from "../utils/historyFilterClears";
import { IntegrationFilterOption } from "./integrationActors";
import {
    isSeriesPicked,
    SeriesFilterItem,
    toListedSeriesFilterItems,
} from "./actorSeriesFilterItems";

type ActorItem =
    | { type: "support" }
    | { type: "agent"; kind: FilterAgentKind }
    | SeriesFilterItem
    | { type: "integration"; integration: IntegrationFilterOption }
    | {
          type: "member";
          id: number;
          name: string;
          user: Pick<UserSchema, "name" | "email" | "iconColor" | "avatar">;
      };

const SUPPORT_FILTER_ITEM: ActorItem = { type: "support" };

const FILTER_AGENT_KINDS: FilterAgentKind[] = [
    ActorKind.RULE,
    ActorKind.KICK,
    ActorKind.ORGANIZATION,
];

const AGENT_FILTER_ITEMS: Record<FilterAgentKind, ActorItem> = {
    [ActorKind.RULE]: { type: "agent", kind: ActorKind.RULE },
    [ActorKind.KICK]: { type: "agent", kind: ActorKind.KICK },
    [ActorKind.ORGANIZATION]: { type: "agent", kind: ActorKind.ORGANIZATION },
};

interface Props {
    filters: HistoryFilters;
    onChange: (filters: Partial<HistoryFilters>) => void;
    /** The series rows to list, picked into `series`; without them Rule and Kick pick by source. */
    pickableSeries?: readonly ActivitySeries[];
}

/** Updated by's rows, plus the picked rows for the dropdown's avatars. */
export interface ActorFilterRows extends ActivityFilterRows<string> {
    pickedItems: ActorItem[];
    onInputValueChange: (query: string) => void;
}

/** The workspace Updated by: series or agents, Kick Support and members, then connected integrations. */
export function useActorFilterRows({
    filters,
    onChange,
    pickableSeries,
}: Props): ActorFilterRows {
    const [search, setSearch] = useState("");
    const { data: members = [] } = useCurrentWorkspaceMembers();
    const connectedIntegrations = useConnectedIntegrations();
    const isPlatformEnabled = useIsActiveWorkspacePlatformEnabled();
    const agentKinds = useMemo(
        () =>
            FILTER_AGENT_KINDS.filter(
                (kind) => kind !== ActorKind.ORGANIZATION || isPlatformEnabled,
            ),
        [isPlatformEnabled],
    );

    const lowerSearch = search.toLowerCase();

    const isSupportSelected = filters.actorType === ActorType.SUPPORT;

    const memberItemsById = useMemo(() => {
        const map = new Map<number, ActorItem>();
        for (const member of members) {
            map.set(member.user.id, {
                type: "member",
                id: member.user.id,
                name: member.user.name,
                user: member.user,
            });
        }
        return map;
    }, [members]);

    const integrationActorByLabel = useMemo(() => {
        const map = new Map<string, ActorItem>();
        for (const integration of connectedIntegrations) {
            map.set(integration.label, {
                type: "integration",
                integration,
            });
        }
        return map;
    }, [connectedIntegrations]);

    const { allAgentItems, allUserItems, allIntegrationItems } = useMemo(() => {
        const platformAgentItems: ActorItem[] = agentKinds
            .filter((kind) => kind === ActorKind.ORGANIZATION)
            .map((kind) => AGENT_FILTER_ITEMS[kind]);
        const agents: ActorItem[] = pickableSeries
            ? [
                  ...toListedSeriesFilterItems({
                      pickableSeries,
                      pickedSeries: filters.series,
                  }),
                  ...platformAgentItems,
              ]
            : agentKinds.map((kind) => AGENT_FILTER_ITEMS[kind]);
        const users: ActorItem[] = [
            SUPPORT_FILTER_ITEM,
            ...members.flatMap((member) => {
                const memberItem = memberItemsById.get(member.user.id);
                return memberItem ? [memberItem] : [];
            }),
        ];
        const integrations: ActorItem[] = [...integrationActorByLabel.values()];

        return {
            allAgentItems: agents,
            allUserItems: users,
            allIntegrationItems: integrations,
        };
    }, [
        pickableSeries,
        filters.series,
        agentKinds,
        members,
        memberItemsById,
        integrationActorByLabel,
    ]);

    const actorItemsByKey = useMemo(
        () =>
            new Map(
                [...allAgentItems, ...allUserItems, ...allIntegrationItems].map(
                    (item) => [getItemKey(item), item],
                ),
            ),
        [allAgentItems, allUserItems, allIntegrationItems],
    );

    const { userItems, agentItems, integrationItems } = useMemo(() => {
        const filterAgents = (): ActorItem[] => {
            if (!search) {
                return allAgentItems;
            }
            return allAgentItems.filter((item) =>
                getItemLabel(item).toLowerCase().includes(lowerSearch),
            );
        };

        const filterUsers = (): ActorItem[] => {
            if (!search) {
                return allUserItems;
            }
            return allUserItems.filter((item) => {
                if (item.type === "support") {
                    return KICK_SUPPORT_LABEL.toLowerCase().includes(
                        lowerSearch,
                    );
                }
                if (item.type === "member") {
                    return (
                        item.name.toLowerCase().includes(lowerSearch) ||
                        (item.user.email?.toLowerCase().includes(lowerSearch) ??
                            false)
                    );
                }
                return false;
            });
        };

        const filterIntegrations = (): ActorItem[] => {
            if (!search) {
                return allIntegrationItems;
            }
            return allIntegrationItems.filter(
                (item) =>
                    item.type === "integration" &&
                    item.integration.label.toLowerCase().includes(lowerSearch),
            );
        };

        const agents = filterAgents();
        const users = filterUsers();
        const integrations = filterIntegrations();

        return {
            userItems: users,
            agentItems: agents,
            integrationItems: integrations,
        };
    }, [search, lowerSearch, allAgentItems, allUserItems, allIntegrationItems]);

    const isItemSelected = useCallback(
        (item: ActorItem): boolean => {
            switch (item.type) {
                case "support":
                    return isSupportSelected;
                case "agent":
                    return arePredicatesSubsetOf(
                        getSourcePredicatesForActor({ kind: item.kind }),
                        filters.sourcePredicates,
                    );
                case "series":
                    return isSeriesPicked({
                        series: item.series,
                        filters: {
                            series: filters.series,
                            sourcePredicates: filters.sourcePredicates,
                        },
                    });
                case "integration":
                    return arePredicatesSubsetOf(
                        getSourcePredicatesForActor({
                            integration: {
                                sourceTypes: item.integration.sourceTypes,
                                systemSources: item.integration.systemSources,
                            },
                        }),
                        filters.sourcePredicates,
                    );
                case "member":
                    return filters.actorIds?.includes(item.id) ?? false;
            }
        },
        [
            isSupportSelected,
            filters.series,
            filters.sourcePredicates,
            filters.actorIds,
        ],
    );

    const comboboxSelectedValue = useMemo((): ActorItem[] => {
        const out: ActorItem[] = [];
        for (const item of allAgentItems) {
            if (isItemSelected(item)) {
                out.push(item);
            }
        }
        if (isSupportSelected) {
            out.push(SUPPORT_FILTER_ITEM);
        }
        for (const id of filters.actorIds ?? []) {
            if (id === null) {
                continue;
            }
            const memberItem = memberItemsById.get(id);
            if (memberItem) {
                out.push(memberItem);
            }
        }
        for (const integration of connectedIntegrations) {
            const item = integrationActorByLabel.get(integration.label);
            if (item && isItemSelected(item)) {
                out.push(item);
            }
        }
        return out;
    }, [
        allAgentItems,
        isSupportSelected,
        filters.actorIds,
        memberItemsById,
        integrationActorByLabel,
        connectedIntegrations,
        isItemSelected,
    ]);

    const applySelection = useCallback(
        (selectedActors: ActorItem[]) => {
            const actorIds: Array<number | null> = [];
            let actorType: ActorType | undefined;
            const predicateGroups: SourcePredicate[][] = [];
            const series: ActivitySeries[] = [];

            for (const item of selectedActors) {
                switch (item.type) {
                    case "support":
                        actorType = ActorType.SUPPORT;
                        break;
                    case "agent":
                        predicateGroups.push(
                            getSourcePredicatesForActor({ kind: item.kind }),
                        );
                        break;
                    case "series":
                        series.push(item.series);
                        break;
                    case "integration":
                        predicateGroups.push(
                            getSourcePredicatesForActor({
                                integration: {
                                    sourceTypes: item.integration.sourceTypes,
                                    systemSources:
                                        item.integration.systemSources,
                                },
                            }),
                        );
                        break;
                    case "member":
                        actorIds.push(item.id);
                        break;
                }
            }

            const merged = mergePredicates(predicateGroups);

            onChange({
                actorIds: actorIds.length > 0 ? actorIds : undefined,
                actorType,
                sourceTypes: undefined,
                sourcePredicates: merged.length > 0 ? merged : undefined,
                series: series.length > 0 ? series : undefined,
            });
        },
        [onChange],
    );

    const resetFilter = useCallback(() => {
        onChange(CLEARED_ACTOR_FILTERS);
    }, [onChange]);

    const selectedCount = comboboxSelectedValue.length;

    const triggerAppendLabel = useMemo(() => {
        if (selectedCount === 0) {
            return "Updated by";
        }
        if (selectedCount <= 2) {
            return comboboxSelectedValue
                .map((item) => getItemLabel(item))
                .join(", ");
        }
        return `Updated by ${selectedCount}`;
    }, [comboboxSelectedValue, selectedCount]);

    const sections = useMemo(
        (): Array<PickerSectionData<string>> =>
            [
                { key: "agents", items: agentItems },
                {
                    key: "users",
                    title: "Users",
                    selectAll: true,
                    items: userItems,
                },
                {
                    key: "integrations",
                    title: "Integrations",
                    selectAll: true,
                    items: integrationItems,
                },
            ].map((section) => ({
                ...section,
                items: section.items.map(toPickerItem),
            })),
        [agentItems, userItems, integrationItems],
    );

    return {
        list: { sections },
        value: comboboxSelectedValue.map(getItemKey),
        onValueChange: (keys) =>
            applySelection(
                keys.flatMap((key) => {
                    const item = actorItemsByKey.get(key);
                    return item ? [item] : [];
                }),
            ),
        onClear: resetFilter,
        chipLabel: selectedCount > 0 ? triggerAppendLabel : undefined,
        filter: matchesPrefilteredQuery,
        onInputValueChange: setSearch,
        pickedItems: comboboxSelectedValue,
    };
}

export const ActorFilterCombobox: React.FC<Props> = (props) => {
    const rows = useActorFilterRows(props);
    const selectedCount = rows.value.length;

    const triggerValue =
        selectedCount > 0 ? (
            <HStack as="span" gap={8} align="center">
                <AvatarGroup
                    items={rows.pickedItems}
                    getKey={getItemKey}
                    getLabel={getItemLabel}
                    renderAvatar={renderActorIcon}
                    max={3}
                    size="xs"
                />
                <span>{rows.chipLabel}</span>
            </HStack>
        ) : (
            "Updated by"
        );

    return (
        <Picker.Select
            {...rows.list}
            multiple
            searchable
            searchPlaceholder="Search..."
            filter={rows.filter}
            value={rows.value}
            onOpenChange={(open) => {
                if (!open) {
                    rows.onInputValueChange("");
                }
            }}
            onInputValueChange={rows.onInputValueChange}
            onValueChange={rows.onValueChange}
            placeholder="Updated by"
            renderValue={() => triggerValue}
        >
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={rows.onClear}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Select>
    );
};

/** Rows are filtered by the search above (labels and member emails), so the root keeps every row it is given. */
const matchesPrefilteredQuery = () => true;

function toPickerItem(item: ActorItem): PickerItemData<string> {
    return {
        value: getItemKey(item),
        label: getItemLabel(item),
        leading: renderItemIcon(item),
    };
}

function getItemKey(item: ActorItem): string {
    switch (item.type) {
        case "support":
            return "support";
        case "agent":
            return `agent-${item.kind}`;
        case "series":
            return toSeriesFilterValue(item.series);
        case "integration":
            return `integration-${item.integration.label}`;
        case "member":
            return `member-${item.id}`;
    }
}

function getItemLabel(item: ActorItem): string {
    switch (item.type) {
        case "support":
            return KICK_SUPPORT_LABEL;
        case "agent":
            return getAgentFilterLabel(item.kind);
        case "series":
            return SERIES_FILTER_LABELS[item.series];
        case "integration":
            return item.integration.label;
        case "member":
            return item.name;
    }
}

function renderActorIcon(item: ActorItem): React.ReactNode {
    return (
        <AvatarTooltip content={getItemLabel(item)}>
            {renderItemIcon(item)}
        </AvatarTooltip>
    );
}

function renderItemIcon(item: ActorItem): React.ReactNode {
    switch (item.type) {
        case "support":
            return <KickAvatar size="xs" aria-hidden />;
        case "agent":
            return FILTER_AGENT_ICONS[item.kind];
        case "series":
            return SERIES_FILTER_ICONS[item.series];
        case "integration":
            return (
                <InstitutionIcon
                    institution={item.integration.institution}
                    size="xs"
                />
            );
        case "member":
            return <UserAvatar user={item.user} size="xs" aria-hidden />;
    }
}
